// libs
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import CircleXFilled from '@nykaa/ui-components/Icons/circle-x-filled';

// Constants
import {
  SAVINGS_DESC,
  NEW_PRODUCT_TITLE,
  VIEW_BAG_BUTTON_TEXT,
  CONTINUE_BUTTON_TEXT,
  CLOSE_ICON_SIZE,
  SAVINGS_AMOUNT_SYMBOL,
  NON_LOGGED_IN_SAVINGS,
  SINGLE_ITEM_TEXT,
  MULTIPLE_ITEMS_TEXT,
  BAG_INFO_SUFFIX,
  PRODUCT_IMAGE_ALT,
  BAG_ITEM_IMAGE_ALT_PREFIX,
  SIZE_PREFIX,
  DEFAULT_SAVINGS,
  FIRST_ITEM_INDEX,
  SECOND_ITEM_INDEX,
  DEFAULT_ASPECT_RATIO,
  PRODUCT_IMAGE_HEIGHT,
  BAG_ITEM_IMAGE_HEIGHT,
  MAX_BAG_ITEMS_TO_DISPLAY,
} from './constants';

// Styled components
import {
  Container,
  Title,
  ProductRow,
  ProductImage,
  ProductDetails,
  ProductName,
  ProductDesc,
  ProductMeta,
  ProductPriceRow,
  BagInfo,
  BagItemsRow,
  BagItemImage,
  BagItemPlaceholder,
  ActionButtonRow,
  ActionButton,
  SecondaryButton,
} from './styles';

import {
  SavingsBanner,
  SavingsContent,
  SavingsTextTitle,
  SavingsTextDesc,
  SavingsImage,
  CloseIconWrapper,
  SavingSpanTitle,
} from './savingsStyles';

// Components
import Price from 'shared/components/Product/components/Price';

// Hooks and selectors
import { getCartData } from 'shared/store/cart/selectors';
import { useProductInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/hooks';
import { useSelectedSKUImages } from 'shared/store/details/hooks/selectors';
import { isLoggedIn } from 'shared/store/app/selectors';
import { MOBILE_CHECKOUT_URI } from 'shared/constants/uri';
import { useDwebCartContext } from 'shared/components/DwebCart/context';
import { onViewBagClick, onNudgeContinueClick } from './analytics';
import { formatAmount } from 'shared/helpers/numbers/amount';

/**
 * Configuration for cart nudge banners and messages
 */
interface CartNudgeConfig {
  enabled?: boolean;
  mwebBanner?: {
    bannerImage: string;
    bannerImageAspectRatio: number;
  };
  dwebBanner?: {
    bannerImage: string;
    bannerImageAspectRatio: number;
  };
  non_logged_in_savings?: string;
  logged_in_savings?: string;
  minCartCountAfterAtc?: number;
}

/**
 * Props for CartComponent
 */
interface CartComponentProps {
  /** Callback function to hide the cart component */
  onHide?: () => void;
  /** Flag to indicate if the component is rendered on mobile */
  isMobile: boolean;
  /** Configuration from remote config for banners and messages */
  config?: CartNudgeConfig;
}

/**
 * CartComponent displays a summary of the cart with savings information
 * and the newly added product details
 *
 * @param {CartComponentProps} props - Component props
 * @returns {JSX.Element} Cart component with savings banner and product details
 */
function CartComponent({ onHide, isMobile, config }: CartComponentProps) {
  // Get cart data from redux store
  const cartDetails = useSelector(getCartData);
  const isLoggedInUser = useSelector(isLoggedIn);
  const images = useSelectedSKUImages();
  const { showCart } = useDwebCartContext();

  // Get theme for styling
  const theme = useTheme();

  // Get product information
  const {
    displayTitle,
    displaySubTitle,
    selectedSize,
    displayDiscount,
    displayDiscountedPrice,
    displayPrice,
  } = useProductInfo();

  // Extract cart data with destructuring
  const { cartPartial } = cartDetails || {};
  const { cartTotal } = cartPartial || {};
  // Memoized calculations for cart data
  const totalSavings = useMemo(
    () => cartTotal?.totalSaved || DEFAULT_SAVINGS,
    [cartTotal]
  );

  const handleClick = () => {
    onViewBagClick();
    if (isMobile) {
      window.location.href = MOBILE_CHECKOUT_URI;
    } else {
      showCart();
    }
  };

  const handleContinueClick = () => {
    onNudgeContinueClick();
    onHide?.();
  };
  // Filter out of stock items and memoize the result
  const availableItems = useMemo(
    () => cartPartial?.items?.filter((item: any) => !item.isOutOfStock) || [],
    [cartPartial]
  );

  // Get the first product image
  const productImage = useMemo(
    () => images?.[FIRST_ITEM_INDEX],
    [images]
  );

  // Calculate remaining items count (excluding the first item)
  const remainingItemsCount = useMemo(
    () => availableItems.length - SECOND_ITEM_INDEX,
    [availableItems]
  );

  // Determine singular or plural text for items
  const itemCountText = useMemo(
    () => remainingItemsCount === SECOND_ITEM_INDEX ? SINGLE_ITEM_TEXT : MULTIPLE_ITEMS_TEXT,
    [remainingItemsCount]
  );

  // Format size display text
  const sizeText = useMemo(
    () => selectedSize ? `${SIZE_PREFIX} ${selectedSize.sizeName}` : '',
    [selectedSize]
  );

  // Check if there are multiple items
  const hasMultipleItems = useMemo(
    () => availableItems.length > SECOND_ITEM_INDEX,
    [availableItems]
  );

  // Get remaining items for display
  const remainingItems = useMemo(
    () => availableItems.slice(SECOND_ITEM_INDEX),
    [availableItems]
  );

  // Get items to display (max 5)
  const displayItems = useMemo(
    () => remainingItems.slice(0, MAX_BAG_ITEMS_TO_DISPLAY),
    [remainingItems]
  );

  // Calculate remaining items count beyond displayed items
  const additionalItemsCount = useMemo(
    () => Math.max(0, remainingItems.length - MAX_BAG_ITEMS_TO_DISPLAY),
    [remainingItems]
  );

  // Check if we should show the overlay on last item
  const shouldShowOverlay = useMemo(
    () => additionalItemsCount > 0,
    [additionalItemsCount]
  );

  // Get banner configuration based on device type
  const bannerConfig = useMemo(() => {
    if (isMobile) {
      return config?.mwebBanner || { bannerImage: '', bannerImageAspectRatio: 2 };
    }
    return config?.dwebBanner || { bannerImage: '', bannerImageAspectRatio: 2 };
  }, [isMobile, config]);

  // Get savings message based on login status
  const savingsMessage = useMemo(() => {
    if (isLoggedInUser && config?.logged_in_savings) {
      return config.logged_in_savings;
    }
    return config?.non_logged_in_savings || NON_LOGGED_IN_SAVINGS;
  }, [isLoggedInUser, config]);

  return (
    <>
      {/* Savings Banner Section */}
      <SavingsBanner>
        {/* Close button for mobile view */}
        {isMobile && (
          <CloseIconWrapper onClick={onHide}>
            <CircleXFilled size={CLOSE_ICON_SIZE} color={theme?.colors?.snow100} />
          </CloseIconWrapper>
        )}

        {/* Savings information display */}
        <SavingsImage
          src={bannerConfig.bannerImage}
          aspectRatio={bannerConfig.bannerImageAspectRatio}
        />
        <SavingsContent isLoggedInUser={totalSavings > DEFAULT_SAVINGS}>
          {totalSavings > DEFAULT_SAVINGS ? (
            <>
              <SavingsTextTitle><SavingSpanTitle>
                {`${SAVINGS_DESC} ${SAVINGS_AMOUNT_SYMBOL}${formatAmount(totalSavings)}`}
              </SavingSpanTitle>
              </SavingsTextTitle>
              <SavingsTextDesc>{savingsMessage}</SavingsTextDesc>
            </>
          ) : (
            <SavingsTextDesc>{savingsMessage}</SavingsTextDesc>
          )}
        </SavingsContent>
      </SavingsBanner>

      {/* Main Cart Content Container */}
      <Container>
        {/* Title for newly added product */}
        <Title>{NEW_PRODUCT_TITLE}</Title>

        {/* Product Details Row */}
        <ProductRow moreProduct={hasMultipleItems}>
          {/* Product Image */}
          <ProductImage
            src={productImage?.url || ''}
            alt={PRODUCT_IMAGE_ALT}
            height={PRODUCT_IMAGE_HEIGHT}
            aspectRatio={productImage?.aspect_ratio || DEFAULT_ASPECT_RATIO}
          />

          {/* Product Information */}
          <ProductDetails>
            <ProductName>{displayTitle}</ProductName>
            <ProductDesc>{displaySubTitle}</ProductDesc>
            <ProductMeta>{sizeText}</ProductMeta>

            {/* Product Pricing */}
            <ProductPriceRow>
              <Price
                discount={displayDiscount}
                discountedPrice={displayDiscountedPrice}
                price={displayPrice || 0}
                hidePercentage={false}
              />
            </ProductPriceRow>
          </ProductDetails>
        </ProductRow>

        {/* Additional Bag Items Section - Only show if there are multiple items */}
        {hasMultipleItems && (
          <>
            {/* Bag items count information */}
            <BagInfo>
              {remainingItemsCount} {itemCountText} {BAG_INFO_SUFFIX}
            </BagInfo>

            {/* Display remaining bag item images */}
            <BagItemsRow>
              {displayItems.map((item: any, idx: number) => (
                <BagItemImage
                  key={idx}
                  src={item.imageUrl}
                  alt={`${BAG_ITEM_IMAGE_ALT_PREFIX} ${idx + SECOND_ITEM_INDEX}`}
                  height={BAG_ITEM_IMAGE_HEIGHT}
                  aspectRatio={DEFAULT_ASPECT_RATIO}
                />
              ))}
              {shouldShowOverlay && (
                <BagItemPlaceholder>
                  +{additionalItemsCount} more
                </BagItemPlaceholder>
              )}
            </BagItemsRow>
          </>
        )}
        <ActionButtonRow>
          <SecondaryButton
            data-test="cart-nudge-continue-btn"
            onClick={handleContinueClick}
          >
            {CONTINUE_BUTTON_TEXT}
          </SecondaryButton>
          <ActionButton
            data-test="cart-nudge-view-bag-btn"
            onClick={handleClick}
          >
            {VIEW_BAG_BUTTON_TEXT}
          </ActionButton>
        </ActionButtonRow>
      </Container>
    </>
  );
}

export default memo(CartComponent);
